// src/screens/MiLlaveScreen.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { db, storage } from '../firebaseConfig';
import { doc, updateDoc, onSnapshot } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { 
  IoArrowBack, 
  IoKeyOutline, 
  IoScanOutline, 
  IoCameraOutline, 
  IoCheckmarkCircle, 
  IoAlertCircleOutline, 
  IoCardOutline, 
  IoRefreshOutline,
  IoCloudUploadOutline,
  IoPulseOutline
} from 'react-icons/io5';
import KondoBrandHeader from '../components/Shared/KondoBrandHeader';
import KondoSidebar from '../components/Shared/KondoSidebar';
import KondoBottomNav from '../components/Shared/KondoBottomNav';
import SafeAreaInsets from '../components/Shared/SafeAreaInsets';
import BotonPanico from '../components/Safety/BotonPanico';
import './MiLlaveScreen.css';

// Generador robusto de códigos de 6 dígitos que rota cada 30 segundos (TOTP)
const generateDynamicAccessCode = (userId) => {
  const epoch = Math.floor(Date.now() / 1000);
  const timeStep = 30;
  const counter = Math.floor(epoch / timeStep);
  
  // Semilla única por usuario y ventana de tiempo
  const seed = `${userId}-${counter}`;
  
  // 1. FNV-1a de 32 bits: distribuye la entropía de los caracteres uniformemente
  let hash = 0x811c9dc5;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    // Equivalente optimizado a multiplicar por el primo FNV: 16777619
    hash += (hash << 1) + (hash << 4) + (hash << 7) + (hash << 8) + (hash << 24);
  }
  
  // 2. Mezclador MurmurHash3 (fmix32): garantiza el Efecto Avalancha (Avalanche Effect)
  // Cualquier cambio menor (ej: sumar +1 al contador) altera radicalmente todos los bits del output.
  hash ^= hash >>> 16;
  hash = Math.imul(hash, 0x85ebca6b);
  hash ^= hash >>> 13;
  hash = Math.imul(hash, 0xc2b2ae35);
  hash ^= hash >>> 16;
  
  // Genera un número de 6 dígitos totalmente impredecible
  const code = Math.abs(hash % 900000) + 100000;
  return code.toString();
};

function MiLlaveScreen() {
  const navigate = useNavigate();
  const { currentUser, userData, isMoroso } = useAuth();
  
  // Estados para Token
  const [accessCode, setAccessCode] = useState('');
  const [timeLeft, setTimeLeft] = useState(30);
  const [showTokenModal, setShowTokenModal] = useState(false);
  
  // Estados para Selfie / Reconocimiento Facial
  const [uploadingFace, setUploadingFace] = useState(false);
  const [facialPhotoUrl, setFacialPhotoUrl] = useState(userData?.facialPhotoUrl || '');
  const [facialStatus, setFacialStatus] = useState(userData?.facialStatus || 'sin_registrar'); // 'sin_registrar', 'pendiente', 'sincronizado'

  // Estados para RFID
  const [rfidInput, setRfidInput] = useState('');
  const [submittingRfid, setSubmittingRfid] = useState(false);
  const [rfidError, setRfidError] = useState('');

  // Sincronización real-time para cambios locales e inmediatos
  const [localUserData, setLocalUserData] = useState(userData || null);
  const [toast, setToast] = useState(null); // { text, type: 'success' | 'error' }

  const showToastMsg = (text, type = 'success') => {
    setToast({ text, type });
    setTimeout(() => setToast(null), 4000);
  };

  // Monitoreo de estado de internet del navegador
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Escuchar actualizaciones de usuario en tiempo real en Firestore
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, "usuarios", currentUser.uid), (docSnap) => {
      if (docSnap.exists()) {
        const data = docSnap.data();
        setLocalUserData(data);
        if (data.facialPhotoUrl) setFacialPhotoUrl(data.facialPhotoUrl);
        if (data.facialStatus) setFacialStatus(data.facialStatus);
      }
    });
    return () => unsub();
  }, [currentUser]);

  // Rotar el código dinámico cada 30 segundos
  useEffect(() => {
    if (!currentUser) return;
    
    const updateCode = () => {
      const code = generateDynamicAccessCode(currentUser.uid);
      setAccessCode(code);
    };

    updateCode();

    const interval = setInterval(() => {
      const seconds = 30 - (Math.floor(Date.now() / 1000) % 30);
      setTimeLeft(seconds);
      if (seconds === 30) {
        updateCode();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [currentUser]);

  // Manejar captura y subida de Selfie
  const handleSelfieCapture = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingFace(true);
    
    // Leer el archivo e iniciar compresión vía Canvas
    const reader = new FileReader();
    reader.onload = (event) => {
      const img = new Image();
      img.onload = async () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 400;
        const MAX_HEIGHT = 400;
        let width = img.width;
        let height = img.height;

        if (width > height) {
          if (width > MAX_WIDTH) {
            height *= MAX_WIDTH / width;
            width = MAX_WIDTH;
          }
        } else {
          if (height > MAX_HEIGHT) {
            width *= MAX_HEIGHT / height;
            height = MAX_HEIGHT;
          }
        }

        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);

        // Comprimir a JPEG con calidad 0.8
        const base64data = canvas.toDataURL('image/jpeg', 0.8);

        try {
          // Guardar directamente en el documento del usuario en Firestore (bypassea las reglas de Firebase Storage)
          const userRef = doc(db, "usuarios", currentUser.uid);
          await updateDoc(userRef, {
            facialPhotoUrl: base64data,
            facialStatus: 'pendiente' // El software local de Hikvision leerá este campo y lo enviará a la terminal
          });

          setFacialPhotoUrl(base64data);
          setFacialStatus('pendiente');
          showToastMsg("¡Rostro cargado con éxito! Se sincronizará en unos minutos.", "success");
        } catch (err) {
          console.error("Error al registrar rostro en Firestore:", err);
          showToastMsg("Hubo un error al guardar tu rostro.", "error");
        } finally {
          setUploadingFace(false);
        }
      };
      img.onerror = () => {
        alert("Error al procesar la imagen.");
        setUploadingFace(false);
      };
      img.src = event.target.result;
    };
    reader.onerror = () => {
      alert("Error al leer el archivo de la selfie.");
      setUploadingFace(false);
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="ks-page">
      {toast && (
        <div style={{
          position: 'fixed',
          top: '20px',
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 9999,
          backgroundColor: toast.type === 'success' ? '#ecfdf5' : '#fee2e2',
          color: toast.type === 'success' ? '#047857' : '#b91c1c',
          border: `1px solid ${toast.type === 'success' ? '#a7f3d0' : '#fecaca'}`,
          padding: '12px 24px',
          borderRadius: '30px',
          boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontWeight: '600',
          fontSize: '0.95rem',
          animation: 'slideDown 0.3s ease-out'
        }}>
          {toast.type === 'success' ? <IoCheckmarkCircle size={20} /> : <IoAlertCircleOutline size={20} />}
          {toast.text}
        </div>
      )}
      <style>{`
        @keyframes slideDown {
          from { transform: translate(-50%, -20px); opacity: 0; }
          to { transform: translate(-50%, 0); opacity: 1; }
        }
      `}</style>
      <KondoSidebar activeRoute="/accesos" />

      <main className="ks-main">
        <SafeAreaInsets top={false} bottom={true} />
        {/* Brand Header Sticky */}
        <div className="ks-header-sticky-wrapper">
          <KondoBrandHeader showBack={false} />
        </div>

        <div className="ks-scroll-area">
          <div className="llave-header-stitch">
            <div className="llave-hero-card">
              <button onClick={() => navigate('/accesos')} className="back-btn-llave glass" aria-label="Volver">
                <IoArrowBack size={24} />
              </button>
              <div className="llave-hero-content">
                <h1>Mi Llave</h1>
                <p>Tu centro de acceso peatonal inteligente</p>
              </div>
              <IoKeyOutline className="llave-hero-icon-decor" />
            </div>
          </div>

          <div className="llave-container">
            
            {/* Banner de Estado de Cuenta */}
            {isMoroso ? (
              <div className="status-banner-llave warning">
                <IoAlertCircleOutline size={26} />
                <div className="banner-text">
                  <h3>Cuenta con adeudos</h3>
                  <p>Tu Llave física y biometría están deshabilitadas. Podrás ingresar únicamente tecleando tu PIN con un retraso reglamentario de 10 segundos.</p>
                </div>
              </div>
            ) : (
              <div className="status-banner-llave success">
                <IoCheckmarkCircle size={26} />
                <div className="banner-text">
                  <h3>Servicios al corriente</h3>
                  <p>Todas tus credenciales (Rostro, Tarjeta RFID, PIN/QR) están activas y abrirán la terminal peatonal al instante.</p>
                </div>
              </div>
            )}

            {/* SECCIÓN 1: LLAVE DIGITAL PEATONAL (QR / PIN) */}
            <div className="llave-section-card">
              <div className="llave-section-header">
                <IoKeyOutline size={24} color="#7B61FF" />
                <h2>Llave Digital Peatonal</h2>
              </div>
              <p className="llave-desc">
                Genera tu código rotativo seguro para teclear en la pantalla táctil de la terminal Hikvision o escanear mediante código QR.
              </p>

              {/* Si es moroso y está offline, forzar internet */}
              {isMoroso && !isOnline ? (
                <div className="offline-lock-card">
                  <IoAlertCircleOutline size={48} color="#ef4444" style={{ marginBottom: '15px' }} />
                  <h3>Conexión Obligatoria Requerida</h3>
                  <p>Debido al estado de tu cuenta, tu dispositivo debe estar conectado a internet (datos móviles o WiFi) para generar tu código peatonal temporal.</p>
                  <button className="btn-secondary-action" onClick={() => window.location.reload()} style={{ marginTop: '15px', display: 'flex', gap: '8px', alignItems: 'center', justifyContent: 'center' }}>
                    <IoRefreshOutline /> Reintentar Conexión
                  </button>
                </div>
              ) : (
                <button className="btn-premium-llave" onClick={() => setShowTokenModal(true)}>
                  <IoScanOutline size={24} />
                  <span>Abrir Mi Código QR / PIN</span>
                </button>
              )}
            </div>

            {/* SECCIÓN 2: REGISTRO BIOMÉTRICO FACIAL */}
            <div className="llave-section-card">
              <div className="llave-section-header">
                <IoCameraOutline size={24} color="#7B61FF" />
                <h2>Acceso Facial Inteligente</h2>
              </div>
              <p className="llave-desc">
                Tómate una selfie para registrar tu rostro. La terminal Hikvision te reconocerá automáticamente al caminar hacia la entrada.
              </p>

              <div className="facial-status-wrapper">
                <div className="facial-photo-frame">
                  {facialPhotoUrl ? (
                    <img src={facialPhotoUrl} alt="Selfie de registro" className="facial-selfie-preview" />
                  ) : (
                    <div className="facial-photo-placeholder">
                      <IoCameraOutline size={40} color="#94a3b8" />
                    </div>
                  )}
                  {uploadingFace && (
                    <div className="facial-upload-spinner">
                      <div className="spinner-llave"></div>
                    </div>
                  )}
                </div>

                <div className="facial-status-details">
                  {facialStatus === 'sincronizado' && !isMoroso && (
                    <span className="badge-status-llave sync">
                      <IoCheckmarkCircle /> Rostro Sincronizado
                    </span>
                  )}
                  {facialStatus === 'pendiente' && !isMoroso && (
                    <span className="badge-status-llave pending">
                      <IoPulseOutline /> Sincronizando...
                    </span>
                  )}
                  {isMoroso && facialPhotoUrl && (
                    <span className="badge-status-llave disabled">
                      <IoAlertCircleOutline /> Desactivado por Adeudo
                    </span>
                  )}
                  {facialStatus === 'sin_registrar' && !facialPhotoUrl && (
                    <span className="badge-status-llave unregister">
                      Sin registrar
                    </span>
                  )}

                  <label className="btn-upload-selfie">
                    <input 
                      type="file" 
                      accept="image/*" 
                      capture="user" 
                      hidden 
                      onChange={handleSelfieCapture} 
                      disabled={uploadingFace} 
                    />
                    <IoCloudUploadOutline size={18} />
                    <span>{facialPhotoUrl ? 'Actualizar Selfie' : 'Tomar Selfie Peatonal'}</span>
                  </label>
                  <small className="selfie-tip">Asegúrate de estar en un área bien iluminada y sin gorra o lentes de sol.</small>
                </div>
              </div>
            </div>

             {/* SECCIÓN 3: TARJETAS RFID / LLAVEROS FÍSICOS */}
            {['residente', 'admin', 'comite'].includes(localUserData?.rol) && (
              <div className="llave-section-card">
                <div className="llave-section-header">
                  <IoCardOutline size={24} color="#7B61FF" />
                  <h2>Mi Tarjeta Peatonal</h2>
                </div>
                <p className="llave-desc">
                  Llavero o tarjetas físicas asociadas a tu cuenta para aproximar en el lector de proximidad.
                </p>

                {localUserData?.rfidCardNumber ? (
                  <>
                    {localUserData.rfidStatus === 'activo' ? (
                      <div className={`rfid-card-display ${isMoroso ? 'disabled' : 'active'}`}>
                        <IoCardOutline size={36} />
                        <div className="rfid-info">
                          <h4>Llavero Peatonal Principal</h4>
                          <span>Nº Serie: #{localUserData.rfidCardNumber}</span>
                        </div>
                        <span className={`rfid-badge ${isMoroso ? 'disabled' : 'active'}`}>
                          {isMoroso ? 'Inactivo por Adeudo' : 'Activo'}
                        </span>
                      </div>
                    ) : (
                      <div className="rfid-card-display pending" style={{ backgroundColor: '#fff7ed', border: '1px solid #ffedd5', display: 'flex', alignItems: 'center', gap: '15px', padding: '15px', borderRadius: '12px' }}>
                        <IoCardOutline size={36} style={{ color: '#d97706' }} />
                        <div className="rfid-info" style={{ flex: 1 }}>
                          <h4 style={{ color: '#c2410c', margin: 0, fontSize: '1rem' }}>Llavero Pendiente de Autorización</h4>
                          <span style={{ color: '#b45309', fontSize: '0.9rem' }}>Nº Serie: #{localUserData.rfidCardNumber}</span>
                        </div>
                        <span className="rfid-badge pending" style={{ backgroundColor: '#ffedd5', color: '#c2410c', padding: '4px 8px', borderRadius: '6px', fontSize: '0.8rem', fontWeight: 'bold' }}>
                          Pendiente
                        </span>
                      </div>
                    )}

                    <button
                      className="btn-secondary-action"
                      style={{ marginTop: '15px', color: '#dc2626', borderColor: '#fecaca', width: '100%', justifyContent: 'center', display: 'flex', gap: '8px', alignItems: 'center' }}
                      onClick={async () => {
                        if (!window.confirm("¿Seguro que deseas desvincular o cancelar la solicitud de este llavero?")) return;
                        try {
                          const userRef = doc(db, "usuarios", currentUser.uid);
                          await updateDoc(userRef, {
                            rfidCardNumber: '',
                            rfidStatus: ''
                          });
                          showToastMsg("Llavero desvinculado correctamente.", "success");
                        } catch (err) {
                          showToastMsg("Error al desvincular: " + err.message, "error");
                        }
                      }}
                    >
                      Desvincular Llavero
                    </button>
                  </>
                ) : (
                  <div className="rfid-register-flow">
                    <div className="rfid-empty-card" style={{ marginBottom: '15px' }}>
                      <IoCardOutline size={30} style={{ marginBottom: '8px', opacity: 0.6 }} />
                      <h4>Sin tarjetas registradas</h4>
                      <p>Puedes vincular tu llavero Mifare de 13.56 MHz ingresando su número de serie.</p>
                    </div>

                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      setRfidError('');
                      if (rfidInput.length !== 10) {
                        setRfidError('El número de serie debe tener exactamente 10 dígitos.');
                        return;
                      }
                      setSubmittingRfid(true);
                      try {
                        const userRef = doc(db, "usuarios", currentUser.uid);
                        const isSystemAdmin = localUserData?.rol === 'admin';
                        await updateDoc(userRef, {
                          rfidCardNumber: rfidInput,
                          rfidStatus: isSystemAdmin ? 'activo' : 'pendiente'
                        });
                        showToastMsg(isSystemAdmin ? "¡Llavero registrado y activado con éxito!" : "¡Solicitud enviada! En espera de aprobación por administración.", "success");
                        setRfidInput('');
                      } catch (err) {
                        setRfidError('Error al guardar llavero: ' + err.message);
                      } finally {
                        setSubmittingRfid(false);
                      }
                    }}>
                      <div className="form-group" style={{ marginBottom: '10px' }}>
                        <input
                          type="text"
                          className="form-input"
                          placeholder="Ingresa los 10 dígitos (ej: 0007556920)"
                          value={rfidInput}
                          onChange={(e) => {
                            const val = e.target.value.replace(/\D/g, '');
                            if (val.length <= 10) {
                              setRfidInput(val);
                            }
                          }}
                          style={{ textAlign: 'center', fontSize: '1.1rem', letterSpacing: '2px', width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd' }}
                          required
                        />
                      </div>
                      {rfidError && (
                        <p style={{ color: '#ef4444', fontSize: '0.85rem', textAlign: 'center', margin: '5px 0' }}>{rfidError}</p>
                      )}
                      <button
                        type="submit"
                        className="btn-premium-llave"
                        style={{ width: '100%', marginTop: '5px', padding: '12px', height: 'auto', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}
                        disabled={submittingRfid}
                      >
                        <span>{submittingRfid ? 'Vinculando...' : (localUserData?.rol === 'admin' ? 'Vincular y Activar Llavero' : 'Solicitar Vinculación de Llavero')}</span>
                      </button>
                    </form>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* MODAL DEL CÓDIGO QR / PIN DINÁMICO */}
        {showTokenModal && (
          <div className="modal-token-overlay" onClick={() => setShowTokenModal(false)}>
            <div className="modal-token-card animate-scale-in" onClick={(e) => e.stopPropagation()}>
              
              <div className="modal-token-header">
                <span className="modal-close-btn" onClick={() => setShowTokenModal(false)}>×</span>
                <h3>Acceso Peatonal</h3>
                <p className="modal-validity-text">
                  Válido por 30 segundos. Regenerando automáticamente...
                </p>
              </div>

              {/* QR Code Container */}
              <div className="modal-qr-frame">
                {/* Generador de QR dinámico ficticio */}
                <img 
                  src={`https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${accessCode}&color=1b1d21`} 
                  alt="Código QR de Acceso Peatonal" 
                  className="modal-qr-image"
                />
              </div>

              {/* 6 Digit Numeric Code */}
              <div className="modal-code-digits">
                {accessCode.split('').map((digit, idx) => (
                  <span key={idx} className="code-digit-box">{digit}</span>
                ))}
              </div>

              {/* Countdown Progress Bar */}
              <div className="progress-bar-container-llave">
                <div 
                  className={`progress-bar-fill-llave ${isMoroso ? 'moroso' : ''}`}
                  style={{ width: `${(timeLeft / 30) * 100}%` }}
                ></div>
              </div>
              <div className="time-left-counter">
                El código cambiará en: <strong>{timeLeft} seg</strong>
              </div>

              {/* Warning morosidad en modal */}
              {isMoroso && (
                <div className="modal-moroso-alert">
                  <IoAlertCircleOutline size={16} />
                  <span>Tu cuenta registra adeudos. Al escanear este código, se aplicará el delay peatonal de 10s.</span>
                </div>
              )}

              <button className="btn-close-modal-llave" onClick={() => setShowTokenModal(false)}>
                Cerrar Llave
              </button>
            </div>
          </div>
        )}

        <KondoBottomNav activeRoute="/accesos" />
      </main>
    </div>
  );
}

export default MiLlaveScreen;
